import { Avatar, Box, Button, Card, Stack, Typography } from '@mui/material'
import { ButtonStyle, CardInfo, CardImg } from './style'
import { formatNumber } from '../../../../common/FormatNumber'
import { IconParticipationProjects } from '../../../../icons/IconParticipationProjects'
import { StyledBadge } from '../../../ui-kit'
import { SocialOrderInfoProps } from '../../../../types/entitys/socialOrders'
import { routesProfile } from '../../../../consts'
import { NavLink, useNavigate } from 'react-router-dom'
import { MouseEvent } from 'react'
import { formatApiDateToDdMmYyyy } from '../../../../utils/common'
import { SocialOrderLikeControl } from '../../SocialOrderLikeControl'
import { useSocialOrderEngagement } from '../../../../hooks/useSocialOrderEngagement'
import {
  canNavigateToUserProfile,
  getUserInitialsFromDisplayName,
  resolveUserAvatarUrl,
  resolveUserDisplayName,
} from '../../../../utils/deletedUserDisplay'

export const SocialOrderInfoDesktop = ({ socialOrder }: SocialOrderInfoProps) => {
  const navigate = useNavigate()
  const cacheKey = socialOrder.slug || socialOrder.id
  const {
    isAuthor,
    isSubscribed,
    subscribersCount,
    isLiked,
    likesCount,
    toggleSubscribe,
    toggleLike,
    isSubscribeLoading,
    isLikeLoading,
  } = useSocialOrderEngagement({ socialOrder, cacheKey })
  const authorName = resolveUserDisplayName(socialOrder.author)
  const viewsCount = socialOrder.counters.views
  const focusLabel = Array.isArray(socialOrder.focus) ? socialOrder.focus.join(', ') : ''

  const handleOpenAuthor = () => {
    if (!canNavigateToUserProfile(socialOrder.author)) return
    navigate(`/${routesProfile.USERS.path}/${socialOrder.author?.slug}`)
  }

  const handleToggleSubscribe = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    void toggleSubscribe()
  }

  const handleCreateProjectFromSocialOrder = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    navigate(`/${routesProfile.PROJECTS.path}/create?socialOrderId=${socialOrder.id}`)
  }

  return (
    <Card variant="primaryCard" sx={CardInfo}>
      <Stack direction="column" gap={2}>
        <Typography variant="black-16-semiBold">Соцзаказ</Typography>
        <Stack
          direction="row"
          onClick={handleOpenAuthor}
          sx={{ cursor: canNavigateToUserProfile(socialOrder.author) ? 'pointer' : 'default' }}
        >
          <Box sx={{ mr: 1 }}>
            <StyledBadge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              variant="dot"
              invisible={socialOrder.author?.is_online !== true}
            >
              <Avatar src={resolveUserAvatarUrl(socialOrder.author)} sx={CardImg}>
                {getUserInitialsFromDisplayName(authorName)}
              </Avatar>
            </StyledBadge>
          </Box>
          <Stack direction="column" gap={1} sx={{ mt: '5px', minWidth: 0 }}>
            <Typography variant="black-14-medium" noWrap>{authorName}</Typography>
          </Stack>
        </Stack>
        
        <Stack direction="column" gap={0.5}>
          <Typography variant="black-14-light" sx={{ lineHeight: '18px' }}>
            Создан: {formatApiDateToDdMmYyyy(socialOrder.created_at)}
          </Typography>
          <Typography variant="black-14-light" sx={{ lineHeight: '18px' }}>
            Отредактирован: {formatApiDateToDdMmYyyy(socialOrder.updated_at)}
          </Typography>
          {focusLabel ? (
            <Typography variant="black-14-light" sx={{ lineHeight: '18px' }}>
              Направленность: {focusLabel}
            </Typography>
          ) : null}
        </Stack>
        <Stack direction="column">
          <Typography variant="black-14-medium">Подписчиков: {formatNumber(subscribersCount)}</Typography>
          <Typography variant="black-14-medium">Просмотров: {formatNumber(viewsCount)}</Typography>
        </Stack>
        <Stack direction="column" gap={1} alignItems="flex-start">
          {isAuthor ? (
            <Button
              variant="follow"
              component={NavLink}
              to={`/${routesProfile.SOCIALORDERS.path}/${socialOrder.id}/edit`}
            >
              Редактировать
            </Button>
          ) : (
            <Button
              variant="follow"
              disabled={isSubscribeLoading}
              onClick={handleToggleSubscribe}
            >
              {isSubscribed ? 'Подписан' : 'Подписаться'}
            </Button>
          )}
          <Button
            variant="contained"
            color="secondary"
            sx={ButtonStyle}
            startIcon={<IconParticipationProjects sx={{ width: '24px', height: '24px' }} />}
            onClick={handleCreateProjectFromSocialOrder}
          >
            Создать проект из соцзаказа
          </Button>
          <Box onClick={(e) => e.stopPropagation()}>
            <SocialOrderLikeControl
              socialOrderId={socialOrder.id}
              isLiked={isLiked}
              likesCount={likesCount}
              likesPreview={socialOrder.likes_preview}
              onToggleLike={toggleLike}
              loading={isLikeLoading}
            />
          </Box>
        </Stack>
      </Stack>
    </Card>
  )
}
